/**
 * useBillingGate — lightweight hook that gates a payment action on
 * billing-profile completeness.
 *
 * Usage:
 *   const gate = useBillingGate();
 *   ...
 *   <gate.Modal />
 *   const onPay = async () => {
 *     const ok = await gate.ensureProfile({ purpose: "topup" });
 *     if (!ok) return;            // user cancelled
 *     await checkoutTopup(...);
 *   };
 *
 * The hook handles its own "already filled this session" caching, so
 * the modal only opens the first time the user buys something — unless
 * the backend reports an empty `state_code`, in which case it opens
 * regardless of cache.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { getBillingProfile } from "../../api/payments.api";
import BillingDetailsModal from "./BillingDetailsModal";


export default function useBillingGate() {
  const [open, setOpen] = useState(false);
  const [purpose, setPurpose] = useState("subscribe");
  const resolverRef = useRef(null);
  // session-scoped: once the user fills (or we confirm it's filled),
  // skip subsequent prompts in this tab.
  const okRef = useRef(false);

  const ensureProfile = useCallback(
    async ({ purpose: p = "subscribe" } = {}) => {
      if (okRef.current) return true;
      // Quick server check — if state_code already set, proceed.
      try {
        const res = await getBillingProfile();
        if (res?.data?.state_code) {
          okRef.current = true;
          return true;
        }
      } catch {
        // 401 or network — fall through to opening the modal; the
        // upsert call will surface the same auth error if it persists.
      }
      setPurpose(p);
      setOpen(true);
      return new Promise((resolve) => {
        resolverRef.current = resolve;
      });
    },
    [],
  );

  const handleSaved = useCallback(() => {
    okRef.current = true;
    setOpen(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(false);
  }, []);

  const Modal = useMemo(() => {
    // eslint-disable-next-line react/display-name
    return () => (
      <BillingDetailsModal
        open={open}
        purpose={purpose}
        onSaved={handleSaved}
        onClose={handleClose}
      />
    );
  }, [open, purpose, handleSaved, handleClose]);

  return { ensureProfile, Modal };
}
